import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  Navigate,
  useParams,
  useLocation
} from "react-router-dom";
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import NavDropdown from 'react-bootstrap/NavDropdown';

import axios from 'axios';

// Modal is no longer needed for cameras, but kept if you use it elsewhere
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

// Optional imports for styling/icons:
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

// --- Existing page imports ---
import HomePage from "./pages/HomePage.js";
import Dashboard from "./pages/Dashboard.js";
import JobsPage from "./pages/JobsPage.js";
import JobDetailsPage from "./pages/JobsDetailsPage.js";
import JobEntryPage from "./pages/JobEntryPage.jsx";
import PartEntryPage from "./pages/PartEntryPage.jsx";
import RevisionApprovalPortal from "./pages/RevisionApprovalPortal.jsx";
import ComponentAckPortal from "./pages/ComponentAckPortal.jsx";
import TaxCertificatePortal from "./pages/TaxCertificatePortal.jsx";
import SignPortalPage from "./pages/SignPortalPage.jsx";
import ShopTvDisplay from "./pages/ShopTvDisplay.jsx";
import CutSheetEntryPage from "./pages/CutSheetEntryPage.jsx";
import DxfNesterPage from "./pages/DxfNesterPage.jsx";
import CutListEntryPage from "./pages/CutListEntryPage.jsx";
import MaterialEntryPage from "./pages/MaterialEntryPage.jsx";
import SteelMarketPage from "./pages/SteelMarketPage.jsx";
import BeaconsPage, { BeaconsNavItem } from "./pages/BeaconsPage.jsx";
import ShopMapPage from "./pages/ShopMapPage.jsx";
import SecurityAlertsPage from "./pages/SecurityAlertsPage.jsx";
import DoorsPage from "./pages/DoorsPage.jsx";
import ShopPhonePage from "./pages/ShopPhonePage.jsx";
import SensorsPage from "./pages/SensorsPage.jsx";
import CheckInPage from "./pages/CheckInPage.jsx";
import FaceSignInPage from "./pages/FaceSignInPage.jsx";
import JobQueuePage from "./pages/JobQueuePage.jsx";
import CustomerEntryPage from "./pages/CustomerEntryPage.jsx";
import PurchaseOrdersPage from "./pages/PurchaseOrdersPage.jsx";
import AdminPurchaseOrdersPage from "./pages/AdminPurchaseOrdersPage.jsx";
import AdminDataTransferPage from "./pages/AdminDataTransferPage.jsx";
import ShippingQueuePage from "./pages/ShippingQueuePage.jsx";
import LaserCurrentPackagingPage from "./pages/LaserCurrentPackagingPage.jsx";
import UserManagementPage from "./pages/UserManagementPage.jsx";
import SchedulingPage from "./pages/SchedulingPage.jsx";
import VendorManagementPage from "./pages/VendorManagementPage.jsx";
import ReceivingPage from "./pages/ReceivingPage.jsx";
import AssembliesManagerPage from "./pages/AssembliesManagerPage.jsx";
import InvoicesModule from "./pages/InvoicesModule.jsx";
import FinancePage from "./pages/FinancePage.jsx";
import ConsumablesEntryPage from "./pages/ConsumablesEntryPage.jsx";
import Progress from "./pages/Progress.js";
import QuoteManagementPage from "./pages/QuoteManagementPage.jsx";
import QuoteRequestsPage from "./pages/QuoteRequestsPage.jsx";
import ReportBuilder from "./pages/ReportBuilder.jsx";
import config from './config.js';
import ShopLifeFeed from './components/ShopLifeFeed';
import CustomerPOQueuePage from "./pages/CustomerPOQueuePage.jsx";
import GaugeManagementPage from "./pages/GaugeManagementPage.jsx";
import PfmeaSetupPage from "./pages/PfmeaSetupPage.jsx";
import ShopHealthPage from "./pages/ShopHealthPage.jsx";
import CamerasPage from "./pages/CamerasPage.jsx";
import SalesOrderPage from "./pages/SalesOrderPage.jsx";
import SalesOrderEntryPage from "./pages/SalesOrderEntryPage.jsx";
import AdminDevToolsPage from "./pages/AdminDevToolsPage.jsx";
import PackagingGuidePage from "./pages/PackagingGuidePage.jsx";
import CoatingManagementPage from "./pages/CoatingManagementPage.jsx";
import ServiceRfqPage from "./pages/ServiceRfqPage.jsx";
import ScanResolvePage from "./pages/ScanResolvePage.jsx";
import PalletCheckPage from "./pages/PalletCheckPage.jsx";
import SurplusRackPage from "./pages/SurplusRackPage.jsx";
import ScanWedge from "./components/ScanWedge.jsx";
import { AdminContext, AdminProvider, isAdminSessionActive, requestAdminPin } from './adminSession.jsx';
import { SiteAuthContext } from './siteAuth';
import { MobileModeProvider, useMobileMode } from './mobile/MobileModeContext.jsx';
import { PhoneAwareChrome, PhoneBeaconsRoute, PhoneCamerasRoute, PhoneConsumablesRoute, PhoneDashboardRoute, PhoneHomeRoute, PhoneJobDetailRoute, PhoneJobsRoute, PhoneProgressRoute, PhoneQueueRoute, PhoneReceivingRoute, PhoneCustomersRoute, PhoneFinanceRoute, PhoneInvoicesRoute, PhoneMaterialsRoute, PhonePartsRoute, PhonePurchaseOrdersRoute, PhoneSalesOrdersRoute, PhoneScanRoute, PhoneSchedulingRoute, PhoneSensorsRoute, PhoneShippingRoute, PhoneUsersRoute, PhoneVendorsRoute } from './mobile/PhoneRouting.jsx';
import PhoneNavItems from './mobile/PhoneNavItems.jsx';
import UpdateBanner from './components/UpdateBanner.jsx';
import AiAssistantDrawer from './components/AiAssistantDrawer.jsx';
import FeedbackButton from './components/FeedbackButton.jsx';
import { ThemeProvider, useThemeMode } from './ThemeContext';
import LoginPage from './pages/LoginPage.jsx';

// AiAssistantDrawer + FeedbackButton render on every route by default.
// Suppress them on chromeless kiosk / popup routes so we get a clean
// full-screen display.
function GlobalOverlays() {
  const loc = useLocation();
  const { phoneLayoutFor } = useMobileMode();
  const chromeless =
    loc.pathname.startsWith('/laser/') ||
    loc.pathname.startsWith('/station-feed/');
  // The phone layout has no room for the floating AI / feedback buttons (they would sit on
  // the bottom tabs); phone pages get them back through the More panel in a later phase.
  if (chromeless || phoneLayoutFor(loc.pathname)) return null;
  return (
    <>
      <AiAssistantDrawer />
      <FeedbackButton />
    </>
  );
}


// Where your API lives
const API_BASE = config.API_BASE_URL;

// --- CLOUDFLARE CREDENTIALS ---
// Replace these with the Client ID and Secret you generated in Cloudflare Access > Service Auth
const CF_ACCESS_CLIENT_ID = "f6aa5936667f527290786c8d7b896774.access";
const CF_ACCESS_CLIENT_SECRET = "53e231d91de00cb864cfe49535c6914434a4712bfd3fccfa4a20e3062d5799d8";
const CAMERA_BASE_URL = "https://cameras.kancraftmfg.com"; 

// Helper to get headers easily
const getCfHeaders = () => ({
  'CF-Access-Client-Id': CF_ACCESS_CLIENT_ID,
  'CF-Access-Client-Secret': CF_ACCESS_CLIENT_SECRET
});

// ------------------------------------------------------------------------------------
// 0) Site Auth Context & Provider (replaces Amplify Basic Auth)
// ------------------------------------------------------------------------------------

function SiteAuthProvider({ children }) {
  const [authenticated, setAuthenticated] = useState(null); // null = checking, true/false = known

  useEffect(() => {
    const token = localStorage.getItem('site_token');
    if (!token) {
      setAuthenticated(false);
      return;
    }
    // Verify token with backend
    fetch(`${API_BASE}/auth/site-verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then((res) => {
        if (res.ok) {
          setAuthenticated(true);
        } else {
          localStorage.removeItem('site_token');
          setAuthenticated(false);
        }
      })
      .catch(() => {
        // If backend is unreachable, still allow access with existing token
        // (avoids locking users out during API downtime)
        setAuthenticated(true);
      });
  }, []);

  function handleLogin(token) {
    localStorage.setItem('site_token', token);
    setAuthenticated(true);
  }

  function handleLogout() {
    localStorage.removeItem('site_token');
    setAuthenticated(false);
  }

  // Still checking
  if (authenticated === null) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Not authenticated — show login
  if (!authenticated) {
    return <LoginPage onLogin={handleLogin} apiBaseUrl={API_BASE} />;
  }

  // Authenticated — render app with logout available via context
  return (
    <SiteAuthContext.Provider value={{ handleLogout }}>
      {children}
    </SiteAuthContext.Provider>
  );
}

// ------------------------------------------------------------------------------------
// 1) Admin unlock: AdminContext, AdminProvider and requestAdminPin live in adminSession.jsx
//    (shared with the phone shell; a phone remembers the unlock for 30 days, utils/adminRemember.js).
// ------------------------------------------------------------------------------------
console.log("Using app.jsx version of StationActivityBanner");

function ProtectedRoute({ children }) {
  const { isAdminActive, loginAsAdmin, restoring, rememberNote } = useContext(AdminContext);
  const [status, setStatus] = useState("checking");
  const prompting = useRef(false); // one PIN prompt at a time (a remembered unlock retries in the background)

  useEffect(() => {
    if (isAdminActive) {
      setStatus("verified");
      return;
    }
    // A remembered phone unlock is being asked of JobQ (app start): wait for it, don't ask for the PIN.
    if (restoring || prompting.current) return;
    async function checkOrPrompt() {
      prompting.current = true;
      const userData = await requestAdminPin({ reason: rememberNote });
      prompting.current = false;
      if (isAdminSessionActive()) {
        // the remembered unlock came through while the prompt was open: it stands
        setStatus("verified");
        return;
      }
      if (userData) {
        loginAsAdmin({
          userId: userData.userId,
          userName: userData.userName,
          role: userData.role,
          pin: userData.pin
        }, { remember: userData.remember });
        setStatus("verified");
      } else {
        setStatus("failed");
      }
    }
    checkOrPrompt();
    // rememberNote is read when the prompt opens; a later change must not open another
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminActive, loginAsAdmin, restoring]);

  if (status === "checking") {
    return <div style={{ padding: "1rem" }}>Verifying admin access...</div>;
  }
  if (status === "failed") {
    return <Navigate to="/" replace />;
  }

  return children;
}

// ------------------------------------------------------------------------------------
// 2) Environment Context & Provider
// ------------------------------------------------------------------------------------
export const EnvironmentContext = createContext(null);

function EnvironmentProvider({ children }) {
  const [envInfo, setEnvInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Determine UI environment immediately
    const uiHost = window.location.hostname;
    const uiEnv = uiHost.startsWith('dev.') || uiHost === 'localhost' ? 'dev' : 'prod';

    // Fetch backend environment info
    axios.get(`${config.API_BASE_URL}/environment`)
      .then(response => {
        setEnvInfo({
          ui: uiEnv,
          api: response.data.api_environment,
          db: response.data.database_host
        });
        setIsLoading(false);
      })
      .catch(error => {
        console.error("Failed to fetch environment info:", error);
        // Set a failed state so the UI can react
        setEnvInfo({
          ui: uiEnv,
          api: "error",
          db: "error"
        });
        setIsLoading(false);
      });
  }, []);

  return (
    <EnvironmentContext.Provider value={{ envInfo, isLoading }}>
      {children}
    </EnvironmentContext.Provider>
  );
}

// ------------------------------------------------------------------------------------
// 3) Environment Banner (Simplified)
// ------------------------------------------------------------------------------------
function EnvironmentBanner() {
  const { envInfo, isLoading } = useContext(EnvironmentContext);

  if (isLoading || !envInfo || envInfo.ui !== 'dev') {
    return null; // Don't show anything if loading, failed, or in production
  }
  
  const isMismatched = envInfo.ui !== envInfo.api;

  const bannerStyle = {
    backgroundColor: isMismatched ? '#dc3545' : 'var(--jq-alert-warning-bg, #ffc107)',
    color: isMismatched ? '#fff' : 'var(--jq-alert-warning-text, #212529)',
    textAlign: 'center',
    padding: '5px',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    position: 'sticky',
    top: 0,
    zIndex: 1050,
  };

  return (
    <div style={bannerStyle}>
      {isMismatched ? '⚠️ ENVIRONMENT MISMATCH | ' : 'SANDBOX MODE | '}
      UI: {envInfo.ui} | API: {envInfo.api} | DB: {envInfo.db.split('.')[0]}
    </div>
  );
};

// ------------------------------------------------------------------------------------
// 4) Enhanced Station Banner with Pop-Out Camera Logic
// ------------------------------------------------------------------------------------

// Helper function to compute remaining hours until 5:00 PM
function getRemainingShiftHours() {
  const shiftEnd = new Date();
  // 17:00 in 24-hour time is 5:00 PM
  shiftEnd.setHours(17, 0, 0, 0);

  const now = new Date();
  const diffMs = shiftEnd - now;
  const diffHrs = diffMs / (1000 * 60 * 60);

  return diffHrs > 0 ? diffHrs : 0;
}

function StationCountdownTimer({ estimatedCompletionTime }) {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    if (!estimatedCompletionTime) {
      setTimeLeft("");
      return;
    }
    if (estimatedCompletionTime === "OVERDUE") {
      setTimeLeft("OVERDUE");
      return;
    }

    function tick() {
      const now = Date.now();
      const end = new Date(estimatedCompletionTime).getTime();
      const diffMs = end - now;
      if (diffMs <= 0) {
        setTimeLeft("OVERDUE");
      } else {
        const totalSec = Math.floor(diffMs / 1000);
        const hrs = Math.floor(totalSec / 3600);
        const mins = Math.floor((totalSec % 3600) / 60);
        const secs = totalSec % 60;
        let result = "";
        if (hrs > 0) {
          result += `${hrs}h `;
        }
        result += `${String(mins).padStart(2, "0")}m `;
        result += `${String(secs).padStart(2, "0")}s`;
        setTimeLeft(result);
      }
    }

    tick(); // run once immediately
    const intId = setInterval(tick, 1000);
    return () => clearInterval(intId);
  }, [estimatedCompletionTime]);

  if (!timeLeft) return null;
  if (timeLeft === "OVERDUE") {
    return (
      <div style={{ color: "red", fontSize: "0.75rem", marginTop: 4 }}>
        OVERDUE
      </div>
    );
  }

  return (
    <div style={{ fontSize: "0.75rem", marginTop: 4 }}>
      <strong>Time Left:</strong> {timeLeft}
    </div>
  );
}

function ElapsedStatusTimer({ initialSeconds, currentStatus }) {
  const [elapsedSeconds, setElapsedSeconds] = useState(initialSeconds);

  useEffect(() => {
    setElapsedSeconds(initialSeconds);
  }, [initialSeconds, currentStatus]);

  useEffect(() => {
    const timerId = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, []);

  const h = Math.floor(elapsedSeconds / 3600);
  const m = Math.floor((elapsedSeconds % 3600) / 60);
  const s = elapsedSeconds % 60;

  return (
    <div style={{ fontSize: "0.75rem", marginTop: 4 }}>
      {currentStatus} for{" "}
      {String(h).padStart(2, "0")}:
      {String(m).padStart(2, "0")}:
      {String(s).padStart(2, "0")}
    </div>
  );
}

function formatSecondsToHhMm(sec) {
  if (!sec || sec < 1) return "0h 00m";
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
}

function getColorClass(stationStatus) {
  switch (stationStatus) {
    case "In Progress":
      return "badge bg-success";
    case "Paused":
      return "badge bg-warning text-dark";
    case "Waiting":
      return "badge bg-info text-dark";
    case "Maintenance":
      return "badge bg-danger";
    case "Idle":
    default:
      return "badge bg-secondary";
  }
}

function getPaceTextClass(paceStatus) {
  switch (paceStatus) {
    case "green":
      return "text-success";
    case "yellow":
      return "text-warning";
    case "red":
      return "text-danger";
    default:
      return "";
  }
}

function getLaserLedStyle(status) {
  // Returns styling for the LED indicator
  switch (status) {
    case "In Progress": // Green Light (Running)
      return { color: "#00ff00", textShadow: "0 0 8px #00ff00", icon: "fas fa-circle" };
    case "Maintenance": // Red Light (Fault)
      return { color: "#ff0000", textShadow: "0 0 8px #ff0000", icon: "fas fa-exclamation-circle" };
    default: // Yellow Light (Idle/Paused/Waiting)
      return { color: "#ffcc00", textShadow: "0 0 8px #ffcc00", icon: "far fa-circle" };
  }
}

// ** NEW COMPONENT: DEDICATED POPUP FOR CAMERAS **
function StationCameraPopup() {
  const { stationName } = useParams();
  const [cameraCount, setCameraCount] = useState(1);
  const [maximizedCamIndex, setMaximizedCamIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Helper for grid sizing in popup
  const getGridClass = (count) => {
    if (count === 1) return "col-12";
    if (count === 2) return "col-6";
    return "col-4"; 
  };

  useEffect(() => {
    async function loadConfig() {
      try {
        // Authenticate tunnel and get config
        await fetch(`${CAMERA_BASE_URL}/cameras/config`, {
          headers: getCfHeaders(),
          credentials: 'include'
        });
        
        const res = await fetch(`${CAMERA_BASE_URL}/cameras/config`, {
          headers: getCfHeaders()
        });
        if (res.ok) {
          const data = await res.json();
          setCameraCount(data[stationName] || 1);
        }
      } catch (e) {
        console.error("Popup: Failed to load camera config", e);
      } finally {
        setIsLoading(false);
      }
    }
    loadConfig();
  }, [stationName]);

  if (isLoading) return <div className="text-white p-4">Loading Stream...</div>;

  return (
    <div className="container-fluid bg-dark vh-100 p-3">
        <h4 className="text-white border-bottom pb-2 mb-3">
            {stationName} Live Feed 
            {maximizedCamIndex !== null && <span className="text-muted fs-6 ms-2">(Click image to return to grid)</span>}
        </h4>
        <div className="row g-2 justify-content-center align-items-center">
            {maximizedCamIndex !== null ? (
            // --- MAXIMIZED VIEW ---
            <div className="col-12" style={{ cursor: "zoom-out" }} onClick={() => setMaximizedCamIndex(null)}>
                <div className="border border-white position-relative">
                <img 
                    src={`${CAMERA_BASE_URL}/stream/${stationName}?index=${maximizedCamIndex}&token=${CF_ACCESS_CLIENT_SECRET}`} 
                    alt="Maximized Feed"
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                <div className="position-absolute top-0 start-0 m-3 badge bg-danger fs-5 shadow">
                    Live Feed - Camera {maximizedCamIndex + 1}
                </div>
                </div>
            </div>
            ) : (
            // --- GRID VIEW ---
            [...Array(cameraCount)].map((_, index) => (
                <div 
                key={index} 
                className={getGridClass(cameraCount)}
                style={{ cursor: "zoom-in" }}
                onClick={() => setMaximizedCamIndex(index)}
                >
                <div className="border border-secondary position-relative">
                    <img 
                    src={`${CAMERA_BASE_URL}/stream/${stationName}?index=${index}&token=${CF_ACCESS_CLIENT_SECRET}`}
                    alt={`${stationName} Cam ${index + 1}`}
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                    <span className="position-absolute top-0 start-0 badge bg-dark m-2 opacity-75 border border-secondary">
                    Cam {index + 1}
                    </span>
                </div>
                </div>
            ))
            )}
        </div>
    </div>
  );
}

// ** MODIFIED TO USE POPUP INSTEAD OF MODAL **
function StationActivityBanner({ isMobile }) {
  const [stations, setStations] = useState([
    { stationName: "Laser", status: "Idle" },
    { stationName: "Tap", status: "Idle" },
    { stationName: "Brake", status: "Idle" },
    { stationName: "Fabrication", status: "Idle" },
    { stationName: "Blast", status: "Idle" },
    { stationName: "Finish", status: "Idle" }
  ]);
  const [todayStateMap, setTodayStateMap] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem('jobq-stations-collapsed');
    return stored === null ? true : stored === 'true';
  });
  const [alertsExpanded, setAlertsExpanded] = useState(false);

  // -- Camera State --
  // We only track config here now, viewing is done via window.open
  const [cameraCounts, setCameraCounts] = useState({}); 

  useEffect(() => {
    // PRE-FLIGHT AUTHENTICATION
    async function authenticateTunnel() {
      try {
        await fetch(`${CAMERA_BASE_URL}/cameras/config`, {
          headers: getCfHeaders(),
          credentials: 'include'
        });
        
        // Once auth succeeds, we can load the data
        fetchCameraConfig(); 
        fetchAllStationData();
      } catch (e) {
        console.error("Cloudflare Tunnel Auth Failed", e);
      }
    }

    authenticateTunnel();
    
    const interval = setInterval(fetchAllStationData, 60000);
    return () => clearInterval(interval);
  }, []);

  async function fetchCameraConfig() {
    try {
      const res = await fetch(`${CAMERA_BASE_URL}/cameras/config`, {
        headers: getCfHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setCameraCounts(data);
      }
    } catch (err) {
      console.error("Failed to load camera config", err);
    }
  }

  async function fetchAllStationData() {
    try {
      const activityRes = await fetch(`${API_BASE}/stations/activity`, {
        cache: "no-store",
        headers: {
          "Pragma": "no-cache",
          "Cache-Control": "no-cache"
        }
      });
      if (!activityRes.ok) {
        console.error("Failed to fetch station activity data", activityRes.status);
        return;
      }
      const activityData = await activityRes.json();

      const stateMap = {};
      await Promise.all(
        activityData.map(async (st) => {
          const stationName = st.stationName;
          try {
            const sumRes = await fetch(`${API_BASE}/stations/${stationName}/today_state_summary`);
            if (!sumRes.ok) {
              console.warn(`No summary for station ${stationName}`, sumRes.status);
              stateMap[stationName] = { in_work_seconds: 0, waiting_seconds: 0, idle_seconds: 0, maintenance_seconds: 0 };
            } else {
              const sumData = await sumRes.json();
              stateMap[stationName] = {
                in_work_seconds: sumData.in_work_seconds || 0,
                waiting_seconds: sumData.waiting_seconds || 0,
                idle_seconds: sumData.idle_seconds || 0,
                maintenance_seconds: sumData.maintenance_seconds || 0
              };
            }
          } catch (err) {
            console.error("Error fetching summary for", stationName, err);
            stateMap[stationName] = { in_work_seconds: 0, waiting_seconds: 0, idle_seconds: 0, maintenance_seconds: 0 };
          }
        })
      );

      setStations(activityData);
      setTodayStateMap(stateMap);

      const newAlerts = [];

      const blastStation = activityData.find(s => s.stationName.toLowerCase() === "blast");
      const finishStation = activityData.find(s => s.stationName.toLowerCase() === "finish");
      if (blastStation && finishStation) {
        const blastHours = blastStation.scheduledHoursToday || 0;
        const finishHours = finishStation.scheduledHoursToday || 0;
        const combined = blastHours + finishHours;

        if (combined > 7) {
          newAlerts.push({
            type: "warning",
            message: `Additional operator coverage required: 
              Combined Blast/Finish scheduled hours exceed 7 (currently ${combined.toFixed(1)}h).`
          });
        }

        const remainingShiftHrs = getRemainingShiftHours();
        if (combined > remainingShiftHrs) {
          newAlerts.push({
            type: "warning",
            message: `Additional operator coverage required:
              The combined Blast/Finish load of ${combined.toFixed(1)}h exceeds the remaining shift hours of ${remainingShiftHrs.toFixed(1)}h available before 5:00pm.`
          });
        }
      }

      try {
        const priorityRiskRes = await fetch(`${API_BASE}/priority1/at_risk`);
        if (priorityRiskRes.ok) {
          const p1RiskData = await priorityRiskRes.json();
          p1RiskData.forEach(job => {
            if (job.atRisk) {
              newAlerts.push({
                type: "priority1",
                message: `Priority ${job.priority_level} job at risk: Job #${job.master_job_num} – ${job.job_description}
                  needs ${job.estimatedRemainingHours}h but only ${job.hoursLeftInShift}h remain until 5:00pm.`
              });
            }
          });
        } else {
          console.warn("Failed to fetch Priority 1 at-risk data:", priorityRiskRes.status);
        }
      } catch (err) {
        console.error("Error fetching priority1 at_risk data", err);
      }

      setAlerts(newAlerts);
    } catch (err) {
      console.error("Error fetching station data", err);
    }
  }

  // New function to open the popup window
  const openCameraWindow = (stationName) => {
    // Open a new window with specific dimensions
    window.open(
        `/station-feed/${stationName}`, 
        `Camera-${stationName}`, 
        "width=1000,height=700,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes"
    );
  };

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('jobq-stations-collapsed', String(next));
      return next;
    });
  };

  const statusDotColor = (status) => {
    switch (status) {
      case "In Progress": return "#198754";
      case "Paused": return "#ffc107";
      case "Waiting": return "#0dcaf0";
      case "Maintenance": return "#dc3545";
      case "Idle": default: return "#6c757d";
    }
  };

  return (
    <>
      {/* Always-visible header bar */}
      <div
        className="d-flex align-items-center justify-content-between"
        style={{
          background: "var(--jq-bg-subtle, #f8f9fa)",
          padding: "0.35rem 0.75rem",
          cursor: "pointer",
          borderBottom: collapsed ? "1px solid var(--jq-border, #dee2e6)" : "none",
          userSelect: "none"
        }}
        onClick={toggleCollapsed}
        title={collapsed ? "Expand station status" : "Collapse station status"}
      >
        <div className="d-flex align-items-center gap-2">
          <i className={`fas fa-chevron-${collapsed ? "down" : "up"}`} style={{ fontSize: "0.7rem", color: "var(--jq-text-muted, #666)" }}></i>
          <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--jq-text-muted, #666)" }}>Stations</span>
          {collapsed && (
            <div className="d-flex align-items-center gap-1 ms-2" style={{ flexWrap: "wrap" }}>
              {stations.map(st => {
                const tasks = Array.isArray(st.tasks) ? st.tasks : [];
                const activeTask = tasks.find(t => t.status === "In Progress");
                const jobNum = activeTask ? activeTask.jobNumber : (st.jobNumber || null);
                const isActive = st.status === "In Progress";
                const isPaused = st.status === "Paused";
                return (
                  <div
                    key={st.stationName}
                    className="d-flex align-items-center"
                    style={{
                      padding: "1px 6px",
                      borderRadius: "10px",
                      backgroundColor: "var(--jq-bg-card, #fff)",
                      border: "1px solid var(--jq-border, #dee2e6)",
                      fontSize: "0.7rem",
                      lineHeight: 1.4,
                      gap: "4px"
                    }}
                    title={`${st.stationName}: ${st.status}${jobNum ? ` - Job #${jobNum}` : ''}`}
                  >
                    <span style={{
                      width: 7, height: 7, borderRadius: "50%",
                      backgroundColor: statusDotColor(st.status),
                      display: "inline-block",
                      flexShrink: 0,
                      boxShadow: isActive ? `0 0 4px ${statusDotColor(st.status)}` : "none"
                    }}></span>
                    <span style={{ fontWeight: 600, color: "var(--jq-text-primary, #333)" }}>
                      {st.stationName.length > 5 ? st.stationName.slice(0, 3) : st.stationName}
                    </span>
                    {isActive && jobNum ? (
                      <span style={{ color: "var(--jq-text-muted, #666)" }}>#{jobNum}</span>
                    ) : isPaused && jobNum ? (
                      <span style={{ color: "#ffc107" }}>#{jobNum}</span>
                    ) : (
                      <span style={{ color: "var(--jq-text-muted, #999)", fontStyle: "italic" }}>Idle</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {collapsed && alerts.length > 0 && (
            <span className="badge bg-warning text-dark" style={{ fontSize: "0.7rem" }}>
              {alerts.length} Alert{alerts.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>

      {/* Expandable station content */}
      <div style={{
        overflow: "hidden",
        maxHeight: collapsed ? "0px" : "800px",
        transition: "max-height 0.3s ease",
      }}>
      <div
        style={{
          background: "var(--jq-bg-subtle, #f8f9fa)",
          padding: "0.5rem",
        }}
      >
        <div
          className="d-flex flex-wrap"
          style={{
            border: isMobile ? "none" : "1px solid var(--jq-border, #ccc)",
            padding: isMobile ? "0" : "0.5rem",
            borderRadius: "4px",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "0.5rem" : "0"
          }}
        >
          {stations.map((st) => {
            const daily = todayStateMap[st.stationName] || {
              in_work_seconds: 0,
              waiting_seconds: 0,
              idle_seconds: 0,
              maintenance_seconds: 0
            };
            const tasks = Array.isArray(st.tasks) ? st.tasks : [];

            // --- LASER LED LOGIC ---
            const isLaser = st.stationName === "Laser";
            
            // Use the live color from API if available, otherwise fallback to status logic
            let ledStyle = null;
            
            if (isLaser) {
                if (st.liveLedColor) {
                    // Direct mapping from IoT color
                    if (st.liveLedColor === 'green') {
                        ledStyle = { color: "#00ff00", textShadow: "0 0 8px #00ff00", icon: "fas fa-circle" };
                    } else if (st.liveLedColor === 'red') {
                        ledStyle = { color: "#ff0000", textShadow: "0 0 8px #ff0000", icon: "fas fa-exclamation-circle" };
                    } else {
                        ledStyle = { color: "#ffcc00", textShadow: "0 0 8px #ffcc00", icon: "far fa-circle" };
                    }
                } else {
                    // Fallback to old status-based logic if API field missing
                    ledStyle = getLaserLedStyle(st.status);
                }
            }

            return (
              <div
                key={st.stationName}
                className={`station-badge rounded p-2 ${!isMobile ? 'me-3 mb-2 border' : 'card shadow-sm border-0 mb-2'}`}
                style={{ 
                    width: isMobile ? "100%" : "auto",
                    minWidth: isMobile ? "auto" : "180px",
                    borderColor: "var(--jq-border, #ccc)",
                    backgroundColor: isMobile ? "var(--jq-bg-card, #fff)" : "transparent"
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="fw-bold d-flex align-items-center">
                    {st.stationName}
                    
                    {isLaser && (
                      <i 
                        className={`${ledStyle.icon} ms-2`} 
                        style={{ color: ledStyle.color, textShadow: ledStyle.textShadow }} 
                        title={`Laser Signal: ${st.status}`}
                      ></i>
                    )}
                  </div>
                  
                  {!isMobile && cameraCounts[st.stationName] > 0 && (
                    <i 
                      className="fas fa-external-link-alt text-secondary" 
                      style={{ cursor: 'pointer' }}
                      title={`Pop-Out ${st.stationName} Camera`}
                      onClick={() => openCameraWindow(st.stationName)}
                    ></i>
                  )}
                </div>

                {st.stationName === "Fabrication" && tasks.length > 0 ? (
                  <>
                    <div
                      className={getColorClass(st.status)}
                      style={{ fontSize: "0.85rem", marginTop: 2 }}
                    >
                      {st.status}
                      {tasks.length > 1 && ` (${tasks.length} tasks)`}
                    </div>
                    
                    {(st.status === "Paused" ||
                      st.status === "Idle" ||
                      st.status === "Waiting" ||
                      st.status === "Maintenance") && (
                      <ElapsedStatusTimer
                        initialSeconds={st.elapsedSecondsInState ?? 0}
                        currentStatus={st.status}
                      />
                    )}

                    {tasks.map((task) => {
                      return (
                        <div key={task.taskId} style={{ marginTop: "0.5rem", paddingLeft: "1rem" }}>
                          <div style={{ fontSize: "0.8rem" }}>
                            <strong>Job #{task.jobNumber}</strong>
                            {task.jobDescription ? ` – ${task.jobDescription}` : ""}
                          </div>
                          {task.userName && (
                            <div style={{ fontSize: "0.75rem", marginTop: 2 }}>
                              <i>User: {task.userName}</i>
                            </div>
                          )}
                          {task.status === "In Progress" && task.estimatedCompletionTime && (
                            <StationCountdownTimer
                              estimatedCompletionTime={task.estimatedCompletionTime}
                            />
                          )}
                          {task.status === "Paused" && (
                            <div style={{ fontSize: "0.75rem", marginTop: 2 }}>
                              (Paused)
                            </div>
                          )}
                          {task.status === "Maintenance" && (
                            <div style={{ fontSize: "0.75rem", marginTop: 2, color: '#dc3545' }}>
                              <strong>Maint:</strong> {task.maintenanceReason || 'No reason provided'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <div
                      className={getColorClass(st.status)}
                      style={{ fontSize: "0.85rem", marginTop: 2 }}
                    >
                      {st.status === "Maintenance" ? "Maintenance" :
                       st.status === "Idle" || st.status === "Waiting"
                        ? st.status
                        : tasks.length > 0
                        ? `${st.status} on Job #${tasks[0].jobNumber || ""}`
                        : `${st.status} on ${st.jobNumber || ""}`
                      }
                    </div>

                    {st.status === "Maintenance" && tasks.length > 0 && tasks[0].maintenanceReason && (
                      <div style={{ fontSize: "0.8rem", marginTop: 4, color: '#dc3545' }}>
                        <strong>Reason:</strong> {tasks[0].maintenanceReason}
                      </div>
                    )}

                    {tasks.length > 0 ? (
                      <div style={{ fontSize: "0.8rem", marginTop: 4 }}>
                        {tasks[0].jobDescription}
                      </div>
                    ) : (
                      st.jobDescription && (
                        <div style={{ fontSize: "0.8rem", marginTop: 4 }}>
                          {st.jobDescription}
                        </div>
                      )
                    )}

                    {tasks.length > 0 && tasks[0].userName && (
                      <div style={{ fontSize: "0.75rem", marginTop: 2 }}>
                        <i>User: {tasks[0].userName}</i>
                      </div>
                    )}

                    {st.status === "In Progress" && (
                      <>
                        {tasks.length > 0 && tasks[0].estimatedCompletionTime && (
                          <StationCountdownTimer
                            estimatedCompletionTime={tasks[0].estimatedCompletionTime}
                          />
                        )}
                        {(!tasks.length || !tasks[0].estimatedCompletionTime) &&
                          st.estimatedCompletionTime && (
                            <StationCountdownTimer
                              estimatedCompletionTime={st.estimatedCompletionTime}
                            />
                          )}
                      </>
                    )}

                    {(st.status === "Paused" ||
                      st.status === "Idle" ||
                      st.status === "Waiting" ||
                      st.status === "Maintenance") && (
                      <ElapsedStatusTimer
                        initialSeconds={st.elapsedSecondsInState ?? 0}
                        currentStatus={st.status}
                      />
                    )}
                  </>
                )}

                {typeof st.scheduledHoursToday === "number" && st.scheduledHoursToday >= 0 && (
                  <div style={{ fontSize: "0.8rem", marginTop: 4 }}>
                    Today: {st.scheduledHoursToday.toFixed(1)}h
                    <span className={`ms-2 ${getPaceTextClass(st.paceStatus)}`}>
                      {st.paceStatus === "green" && "On Pace"}
                      {st.paceStatus === "yellow" && "At Risk"}
                      {st.paceStatus === "red" && "Off Pace"}
                    </span>
                  </div>
                )}

                <div
                  style={{ fontSize: "0.7rem", marginTop: 4, color: "var(--jq-text-muted, #666)" }}
                  title={`In Work: ${formatSecondsToHhMm(daily.in_work_seconds)} | Waiting: ${formatSecondsToHhMm(daily.waiting_seconds)} | Idle: ${formatSecondsToHhMm(daily.idle_seconds)} | Maint: ${formatSecondsToHhMm(daily.maintenance_seconds)}`}
                >
                  In Work: {formatSecondsToHhMm(daily.in_work_seconds)} | Idle: {formatSecondsToHhMm(daily.idle_seconds)}
                </div>
              </div>
            );
          })}
          {/* KanCraft #shop-life (components/ShopLifeFeed.jsx): fills the row after the stations; reads only while open. */}
          {!isMobile && <ShopLifeFeed active={!collapsed} />}
        </div>

        {/* Collapsible Alerts Section */}
        <div style={{ marginTop: "0.5rem" }}>
          <div
            className="d-flex align-items-center justify-content-between"
            style={{
              padding: "0.35rem 0.5rem",
              cursor: "pointer",
              borderRadius: "4px",
              border: "1px solid var(--jq-border, #ccc)",
              backgroundColor: "var(--jq-bg-card, #fff)",
              userSelect: "none"
            }}
            onClick={() => setAlertsExpanded(prev => !prev)}
          >
            <div className="d-flex align-items-center gap-2">
              <i className={`fas fa-chevron-${alertsExpanded ? "up" : "down"}`} style={{ fontSize: "0.65rem", color: "var(--jq-text-muted, #666)" }}></i>
              <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--jq-text-primary, #333)" }}>
                Active Alerts ({alerts.length})
              </span>
              {alerts.length > 0 && !alertsExpanded && (
                <span style={{
                  width: 8, height: 8, borderRadius: "50%",
                  backgroundColor: "#dc3545",
                  display: "inline-block",
                  animation: "pulse-dot 1.5s ease-in-out infinite"
                }}></span>
              )}
            </div>
          </div>
          {alertsExpanded && (
            <div style={{ padding: "0.5rem 0.25rem", maxHeight: "200px", overflowY: "auto" }}>
              {alerts.length === 0 ? (
                <div style={{ fontSize: "0.85rem", fontStyle: "italic", color: "var(--jq-text-muted, #666)" }}>
                  No active alerts
                </div>
              ) : (
                alerts.map((alert, idx) => (
                  <div
                    key={idx}
                    style={{
                      marginBottom: "0.5rem",
                      padding: "0.5rem",
                      border: "1px solid var(--jq-alert-warning-border, #ffc107)",
                      borderRadius: "4px",
                      backgroundColor: "var(--jq-alert-warning-bg, #fff3cd)",
                      color: "var(--jq-alert-warning-text, #856404)",
                      fontSize: "0.85rem"
                    }}
                  >
                    {alert.message}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
}

// ------------------------------------------------------------------------------------
// 5) Environment Indicator Dots Component (Full Logic Restored)
// ------------------------------------------------------------------------------------
function EnvironmentIndicatorDots() {
  const { envInfo, isLoading } = useContext(EnvironmentContext);

  const dotStyle = {
    height: '12px',
    width: '12px',
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '5px',
    border: '1px solid rgba(0,0,0,0.2)'
  };
  
  const colors = {
    green: '#28a745',
    yellow: '#ffc107',
    red: '#dc3545',
    grey: '#6c757d' // for loading/error state
  };

  if (isLoading) {
    return (
      <div className="d-flex align-items-center me-3" title="Loading environment status...">
        <span style={{ ...dotStyle, backgroundColor: colors.grey }}></span>
        <span style={{ ...dotStyle, backgroundColor: colors.grey }}></span>
        <span style={{ ...dotStyle, backgroundColor: colors.grey }}></span>
      </div>
    );
  }

  // Determine colors based on environment
  const isMismatched = envInfo.api !== "error" && envInfo.ui !== envInfo.api;
  
  let uiColor = envInfo.ui === 'prod' ? colors.green : colors.yellow;
  
  // Logic for API Color
  let apiColor = envInfo.api === 'prod' ? colors.green : (envInfo.api === 'dev' ? colors.yellow : colors.grey);
  
  // Logic for DB Color
  // Default to yellow (safe/sandbox) unless strictly proven to be Production
  const dbHost = envInfo.db.toLowerCase();
  
  // Known Prod string: "jobq-db" (must NOT contain "dev")
  // Known Dev string: "dev-jobq-db"
  
  const isExplicitDev = dbHost.includes('dev') || dbHost.includes('stage') || dbHost.includes('test');
  
  // Only turn Green if it contains the prod key AND does not contain the dev key.
  const isExplicitProd = dbHost.includes('jobq-db') && !isExplicitDev;

  let dbColor = colors.yellow; // Default to Yellow
  if (isExplicitProd) {
    dbColor = colors.green;
  }
  
  // Error handling override
  if (envInfo.db === 'error') {
    dbColor = colors.grey;
  }
  
  // If mismatched, all dots turn red for high visibility
  if (isMismatched) {
    uiColor = apiColor = dbColor = colors.red;
  }

  return (
    <div className="d-flex align-items-center me-3" style={{ color: 'white', fontSize: '0.7rem' }}>
      <div className="text-center me-2">
        <span style={{ ...dotStyle, backgroundColor: uiColor }} title={`UI Environment: ${envInfo.ui}`}></span>
        <div>UI</div>
      </div>
      <div className="text-center me-2">
        <span style={{ ...dotStyle, backgroundColor: apiColor }} title={`API Environment: ${envInfo.api}`}></span>
        <div>API</div>
      </div>
      <div className="text-center">
        <span style={{ ...dotStyle, backgroundColor: dbColor }} title={`DB Host: ${envInfo.db}`}></span>
        <div>DB</div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------
// 6) NavBar (Restored Full Navigation Logic)
// ------------------------------------------------------------------------------------
function NavBar() {
  const { isAdminActive, logoutAdmin } = useContext(AdminContext);
  const { envInfo } = useContext(EnvironmentContext);
  const { isDark, toggleMode } = useThemeMode();
  const { handleLogout } = useContext(SiteAuthContext);

  return (
    <Navbar bg="dark" variant="dark" expand="lg" className="shadow-sm">
      <div className="container-fluid d-flex align-items-center">

        <EnvironmentIndicatorDots />

        {isAdminActive && (
          <button
            className="btn btn-sm btn-outline-light me-3"
            onClick={logoutAdmin}
          >
            Clear Credentials
          </button>
        )}

        <Navbar.Brand as={Link} to="/" className="fw-bold fs-3">
          JobQ
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="main-navbar-nav" />
        <Navbar.Collapse id="main-navbar-nav">

          <Nav className="ms-auto align-items-center">
            {/* Phone-width screens only (mobile/PhoneNavItems.jsx). */}
            <PhoneNavItems isAdminActive={isAdminActive} />
            <Nav.Link as={Link} to="/">Home</Nav.Link>
            <Nav.Link as={Link} to="/dashboard">Dashboard</Nav.Link>
            <Nav.Link as={Link} to="/cameras">Cameras</Nav.Link>
            <Nav.Link as={Link} to="/jobs">Jobs</Nav.Link>
            <Nav.Link as={Link} to="/scheduling">Scheduling</Nav.Link>
            <Nav.Link as={Link} to="/queue">Job Queue</Nav.Link>
            <Nav.Link as={Link} to="/progress">Progress</Nav.Link>
            <Nav.Link as={Link} to="/shipping-queue">Shipping</Nav.Link>
            <Nav.Link as={Link} to="/receiving">Receiving</Nav.Link>
            <Nav.Link as={Link} to="/customer-po">Customer PO</Nav.Link>

            <NavDropdown
              title={
                <>
                  <i className="fas fa-user-cog me-1"></i> Admin
                </>
              }
              id="admin-nav-dropdown"
              align="end"
              className="admin-mega"
            >
              {/* Three columns of labelled groups, not one run of 26 links.
                  Flat, the menu was 852px tall and ran off a 768px laptop;
                  headers alone made that worse (1022px), so the columns are
                  what pay for them -- 409px, and one glance per group.
                  Columns are read top-to-bottom, then left-to-right; groups
                  follow the order work moves through the shop, and items
                  inside a group follow their own workflow (a Quote Request
                  becomes a Quote becomes a Sales Order becomes an Invoice),
                  never alphabetical. Keep a group at 6 items or fewer -- past
                  that it stops being scannable and wants splitting. */}
              <div className="admin-mega-col">
                <NavDropdown.Header>Engineering</NavDropdown.Header>
                <NavDropdown.Item as={Link} to="/parts">Parts</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/assemblies">Assemblies</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/cut-sheets">Cut Sheets</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/cut-lists">Cut Lists</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/dxf-nester">DXF Nester</NavDropdown.Item>
                {/* Legacy stateless packing preview. Persistent plans live on the
                    Sales Order page; this describes how a finished part is boxed,
                    which is product definition -- hence Engineering. */}
                <NavDropdown.Item as={Link} to="/packaging-guide">Packaging Guide</NavDropdown.Item>

                <NavDropdown.Header>Materials</NavDropdown.Header>
                <NavDropdown.Item as={Link} to="/materials-entry">Materials</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/consumables">Consumables</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/steel-market">Steel Market</NavDropdown.Item>
                <BeaconsNavItem />
                {/* Surplus + owed bundles on the rack, and the pieces to move to a pallet. */}
                <NavDropdown.Item as={Link} to="/surplus-rack">Surplus Rack</NavDropdown.Item>
              </div>

              <div className="admin-mega-col">
                <NavDropdown.Header>Purchasing</NavDropdown.Header>
                <NavDropdown.Item as={Link} to="/purchase-orders">Purchase Orders</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/vendors">Vendors</NavDropdown.Item>
                {/* "Coating Manifests" was the name when Coating was the only thing we sent
                    out. It is one outside process among several now, so the surface is named
                    for what it does. The /coating path still works -- printed paperwork and
                    bookmarks point at it. */}
                <NavDropdown.Item as={Link} to="/outsourcing">Outsourcing</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/service-rfqs">Service RFQs</NavDropdown.Item>
                {/* Expense POs -- software subs, service contracts -- on the isolated
                    /admin_purchase_orders API, nothing to do with material. By ledger
                    they sit with Finance, but anyone hunting one scans for "PO", so
                    they live next to Purchase Orders where people look. */}
                <NavDropdown.Item as={Link} to="/admin-pos">Admin POs</NavDropdown.Item>

                <NavDropdown.Header>Sales &amp; Accounting</NavDropdown.Header>
                <NavDropdown.Item as={Link} to="/quote-requests">Quote Requests</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/quotes">Quotes</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/sales-orders">Sales Orders</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/customers">Customers</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/invoices">Invoices</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/finance">Finance</NavDropdown.Item>
              </div>

              <div className="admin-mega-col">
                <NavDropdown.Header>Quality &amp; Maintenance</NavDropdown.Header>
                <NavDropdown.Item as={Link} to="/gauges">Gauge Management</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/pfmea-setup">PFMEA Setup</NavDropdown.Item>
                {/* Despite the name, this manages maintenance definitions -- shop
                    upkeep schedules and attachments -- not a health dashboard. */}
                <NavDropdown.Item as={Link} to="/shop-health">Shop Health</NavDropdown.Item>
                {/* The compressor shed's temperature (and any shop sensor after it). */}
                <NavDropdown.Item as={Link} to="/sensors">Shop Sensors</NavDropdown.Item>

                <NavDropdown.Header>Reporting &amp; System</NavDropdown.Header>
                <NavDropdown.Item as={Link} to="/reportbuilder">Reports</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/admin-data-transfer">Data Transfer</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/user-management">User Management</NavDropdown.Item>
                {envInfo?.ui === 'dev' && (
                  <NavDropdown.Item as={Link} to="/admin-dev-tools">Dev Tools</NavDropdown.Item>
                )}

                <NavDropdown.Header>Security</NavDropdown.Header>
                <NavDropdown.Item as={Link} to="/security-alerts">Security Alerts</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/doors">Doors</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/shop-phone">Shop Phone</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/face-id">Face Sign-In</NavDropdown.Item>
              </div>
            </NavDropdown>

            <button
              className="theme-toggle-btn ms-2"
              onClick={toggleMode}
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <i className={isDark ? 'fas fa-sun' : 'fas fa-moon'}></i>
            </button>

            <button
              className="btn btn-sm btn-outline-secondary ms-2"
              onClick={handleLogout}
              title="Sign out"
            >
              <i className="fas fa-sign-out-alt"></i>
            </button>
          </Nav>

        </Navbar.Collapse>
      </div>
    </Navbar>
  );
}

// ------------------------------------------------------------------------------------
// 7) NEW COMPONENT: Mobile Menu Page
// ------------------------------------------------------------------------------------
function MobileMenuPage() {
    const btnStyle = {
        height: '80px',
        fontSize: '1.2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '1rem',
        fontWeight: 'bold'
    };

    return (
        <div className="container mt-3">
            <h4 className="mb-3 text-center">Mobile Quick Links</h4>
            <div className="row">
                <div className="col-6">
                    <Link to="/jobs" className="btn btn-primary w-100 shadow-sm" style={btnStyle}>
                        <i className="fas fa-clipboard-list me-2"></i> Jobs
                    </Link>
                </div>
                <div className="col-6">
                    <Link to="/cameras" className="btn btn-dark w-100 shadow-sm" style={btnStyle}>
                        <i className="fas fa-video me-2"></i> Cameras
                    </Link>
                </div>
                <div className="col-6">
                    <Link to="/queue" className="btn btn-success w-100 shadow-sm" style={btnStyle}>
                        <i className="fas fa-layer-group me-2"></i> Queue
                    </Link>
                </div>
                <div className="col-6">
                    <Link to="/shipping-queue" className="btn btn-warning w-100 shadow-sm" style={btnStyle}>
                        <i className="fas fa-truck me-2"></i> Shipping
                    </Link>
                </div>
                <div className="col-6">
                    <Link to="/receiving" className="btn btn-info w-100 shadow-sm" style={btnStyle}>
                        <i className="fas fa-box-open me-2"></i> Receiving
                    </Link>
                </div>
                <div className="col-12">
                     <Link to="/job-entry" className="btn btn-secondary w-100 shadow-sm" style={{...btnStyle, height: '60px'}}>
                        <i className="fas fa-plus-circle me-2"></i> New Job Entry
                    </Link>
                </div>
            </div>
            <div className="text-center mt-3 text-muted">
                <small>Use the menu icon above for full access.</small>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------------------------
// 7b) Today's chrome around the routes. Pages switched on for phones get the phone shell
//     instead (mobile/PhoneRouting.jsx; docs/mobile-pwa-plan.md).
// ------------------------------------------------------------------------------------
function desktopChrome(isMobile) {
  return (children) => (
    <>
      <EnvironmentBanner />
      <NavBar />

      {/* Pass isMobile to Banner to change layout */}
      <StationActivityBanner isMobile={isMobile} />

      <div className="container-fluid my-4">{children}</div>
    </>
  );
}

// ------------------------------------------------------------------------------------
// 8) App (Wraps everything in the new EnvironmentProvider + Mobile Logic)
// ------------------------------------------------------------------------------------
export default function App() {
  // Mobile detection state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // PUBLIC customer revision-approval portal — rendered OUTSIDE the site
  // auth gate and app chrome. The 256-bit token in the URL is the only
  // credential; its API routes are API-key-exempt server-side.
  const isPublicApprovalPortal = window.location.pathname.startsWith("/approve/");
  // Same deal, facing VENDORS instead of customers: a purchased-component
  // revision they acknowledge before cutting metal.
  const isPublicComponentAck = window.location.pathname.startsWith("/component-ack/");
  // Customer sales-tax exemption certificate upload (Kansas ST-28D/A/M). Same
  // token-only pattern; the API side is /portal/tax-certificate/*.
  const isPublicTaxCertificate = window.location.pathname.startsWith("/tax-certificate/");
  // Shop wall TV (Raspberry Pi kiosk): same token-only pattern, API side is
  // GET /portal/display/{token}. No login, no nav, no AI drawer.
  const isShopTv = window.location.pathname.startsWith("/tv/");
  // Pickup / delivery receipt signed on a customer's or driver's own phone from a QR
  // (jobq-api /portal/sign/*). Same token-only pattern: no login, no nav, no service worker.
  const isPublicSign = window.location.pathname.startsWith("/sign/");

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (isPublicApprovalPortal) {
    return <RevisionApprovalPortal />;
  }

  if (isPublicComponentAck) {
    return <ComponentAckPortal />;
  }

  if (isPublicTaxCertificate) {
    return <TaxCertificatePortal />;
  }

  if (isShopTv) {
    return <ShopTvDisplay />;
  }

  if (isPublicSign) {
    return <SignPortalPage />;
  }

  return (
    <ThemeProvider>
    <UpdateBanner />
    <SiteAuthProvider>
    <AdminProvider>
    <MobileModeProvider>
      <BrowserRouter>
        <EnvironmentProvider>
          {/* If the current path is the camera popup, we DO NOT want the banner/nav/etc.
            However, react-router v6 structure here renders everything. 
            The simplest way: The popup opens in a new window at /station-feed.
            We need a Route that captures that and renders ONLY the popup content.
          */}
          
          <Routes>
            {/* The Dedicated Popup Route - Renders cleanly without Nav/Banner */}
            <Route path="/station-feed/:stationName" element={<StationCameraPopup />} />

            {/* Laser-workstation packaging kiosk — chromeless full-screen */}
            <Route path="/laser/current-packaging" element={<LaserCurrentPackagingPage />} />

            {/* Main Application Routes */}
            <Route path="*" element={
              <PhoneAwareChrome desktopChrome={desktopChrome(isMobile)}>
                  <Routes>
                    {/* Home: phone home (phone app on), the mobile menu (phones), or HomePage */}
                    <Route path="/" element={<PhoneHomeRoute fallback={isMobile ? <MobileMenuPage /> : <HomePage />} />} />
                    <Route path="/scan" element={<PhoneScanRoute />} />
                    
                    <Route path="/dashboard" element={<PhoneDashboardRoute fallback={<Dashboard />} />} />
                    {/* Printed QR codes (job travelers): /s/task-37114. Not admin-gated —
                        shop staff scan these, and the pages they open are not gated either. */}
                    <Route path="/s/:code" element={<ScanResolvePage />} />
                    {/* A sales-order pallet beacon's QR (plt-<id>) lands here: anyone on the floor checks the
                        pallet against JobQ. Not admin-gated, like /s/:code. */}
                    <Route path="/pallet-check/:palletId" element={<PalletCheckPage />} />
                    {/* The surplus rack (2026-10-26): bundles + pulls to a pallet. A bundle label's Data Matrix
                        (/s/sb-<id>) lands here. Not admin-gated: every action asks for a PIN. */}
                    <Route path="/surplus-rack" element={<SurplusRackPage />} />
                    <Route path="/jobs" element={<PhoneJobsRoute fallback={<JobsPage />} />} />
                    <Route path="/jobs/:jobId" element={<PhoneJobDetailRoute fallback={<JobDetailsPage />} />} />
                    <Route path="/jobs/new" element={<JobEntryPage />} />
                    <Route path="/job-entry" element={<JobEntryPage />} />
                    <Route
                      path="/cameras"
                      element={
                        <ProtectedRoute>
                          <PhoneCamerasRoute fallback={<CamerasPage />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/scheduling" element={<PhoneSchedulingRoute fallback={<SchedulingPage />} />} />
                    <Route
                      path="/queue"
                      element={<PhoneQueueRoute fallback={<JobQueuePage />} />}
                    />
                    <Route path="/progress" element={<PhoneProgressRoute fallback={<Progress />} />} />
                    <Route path="/shipping-queue" element={<PhoneShippingRoute fallback={<ShippingQueuePage />} />} />
                    <Route path="/receiving" element={<PhoneReceivingRoute fallback={<ReceivingPage />} />} />
                    <Route path="/customer-po" element={<CustomerPOQueuePage />} />
                    <Route
                      path="/parts"
                      element={
                        <ProtectedRoute>
                          <PhonePartsRoute fallback={<PartEntryPage />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/parts/:partId"
                      element={
                        <ProtectedRoute>
                          <PhonePartsRoute fallback={<PartEntryPage />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/cut-sheets"
                      element={
                        <ProtectedRoute>
                          <CutSheetEntryPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dxf-nester"
                      element={
                        <ProtectedRoute>
                          <DxfNesterPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/cut-lists"
                      element={
                        <ProtectedRoute>
                          <CutListEntryPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/cut-sheets/:cutSheetId"
                      element={
                        <ProtectedRoute>
                          <CutSheetEntryPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/materials-entry"
                      element={
                        <ProtectedRoute>
                          <PhoneMaterialsRoute fallback={<MaterialEntryPage />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/steel-market"
                      element={
                        <ProtectedRoute>
                          <SteelMarketPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/beacons"
                      element={
                        <ProtectedRoute>
                          <PhoneBeaconsRoute fallback={<BeaconsPage />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/shop-map"
                      element={
                        <ProtectedRoute>
                          <ShopMapPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/security-alerts"
                      element={
                        <ProtectedRoute>
                          <SecurityAlertsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/doors"
                      element={
                        <ProtectedRoute>
                          <DoorsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/shop-phone"
                      element={
                        <ProtectedRoute>
                          <ShopPhonePage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Any employee's own I'm here / I'm leaving (their typed PIN): NOT admin-gated. */}
                    <Route path="/check-in" element={<CheckInPage />} />
                    {/* Shop sensors (compressor shed temperature). NOT admin-gated: the alert pushes link
                        here; its Admin section asks for the Admin PIN itself (pages/SensorsPage.jsx). */}
                    <Route path="/sensors" element={<PhoneSensorsRoute fallback={<SensorsPage />} />} />
                    {/* Not behind the admin unlock: people enroll with their own PIN, and the page's
                        admin parts ask for the Admin PIN themselves (pages/FaceSignInPage.jsx). */}
                    <Route path="/face-id" element={<FaceSignInPage />} />
                    <Route
                      path="/customers"
                      element={
                        <ProtectedRoute>
                          <PhoneCustomersRoute fallback={<CustomerEntryPage />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/customers/:customerId"
                      element={
                        <ProtectedRoute>
                          <PhoneCustomersRoute fallback={<CustomerEntryPage />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/purchase-orders"
                      element={
                        <ProtectedRoute>
                          <PhonePurchaseOrdersRoute fallback={<PurchaseOrdersPage />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin-pos"
                      element={
                        <ProtectedRoute>
                          <AdminPurchaseOrdersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin-data-transfer"
                      element={
                        <ProtectedRoute>
                          <AdminDataTransferPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin-dev-tools"
                      element={
                        <ProtectedRoute>
                          <AdminDevToolsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/packaging-guide"
                      element={
                        <ProtectedRoute>
                          <PackagingGuidePage />
                        </ProtectedRoute>
                      }
                    />
                    {/* /coating is kept forever, not redirected: it is on printed routing
                        slips and in operators' bookmarks, and a 404 on the shop floor at
                        hand-off time is worse than a slightly stale URL. */}
                    <Route
                      path="/coating"
                      element={
                        <ProtectedRoute>
                          <CoatingManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/outsourcing"
                      element={
                        <ProtectedRoute>
                          <CoatingManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/service-rfqs"
                      element={
                        <ProtectedRoute>
                          <ServiceRfqPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/user-management"
                      element={
                        <ProtectedRoute>
                          <PhoneUsersRoute fallback={<UserManagementPage />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/vendors"
                      element={
                        <ProtectedRoute>
                          <PhoneVendorsRoute fallback={<VendorManagementPage />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/assemblies"
                      element={
                        <ProtectedRoute>
                          <AssembliesManagerPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/invoices"
                      element={
                        <ProtectedRoute>
                          <PhoneInvoicesRoute fallback={<InvoicesModule />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/finance"
                      element={
                        <ProtectedRoute>
                          <PhoneFinanceRoute fallback={<FinancePage />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/quotes"
                      element={
                        <ProtectedRoute>
                          <QuoteManagementPage  />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/quote-requests"
                      element={
                        <ProtectedRoute>
                          <QuoteRequestsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/sales-orders"
                      element={
                        <ProtectedRoute>
                          <PhoneSalesOrdersRoute fallback={<SalesOrderPage />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/sales-order-entry"
                      element={
                        <ProtectedRoute>
                          <SalesOrderEntryPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/consumables"
                      element={
                        <ProtectedRoute>
                          <PhoneConsumablesRoute fallback={<ConsumablesEntryPage />} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/reportbuilder"
                      element={
                        <ProtectedRoute>
                          <ReportBuilder />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/gauges"
                      element={
                        <ProtectedRoute>
                          <GaugeManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/pfmea-setup"
                      element={
                        <ProtectedRoute>
                          <PfmeaSetupPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/shop-health"
                      element={
                        <ProtectedRoute>
                          <ShopHealthPage />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
              </PhoneAwareChrome>
            } />
          </Routes>
        </EnvironmentProvider>
        <GlobalOverlays />
        {/* A USB scanner reading a printed JobQ code opens it from any page (/s/<code>). */}
        <ScanWedge />
      </BrowserRouter>
    </MobileModeProvider>
    </AdminProvider>
    </SiteAuthProvider>
    </ThemeProvider>
  );
}